import type { SupplyIntelConfig } from "./config";
import { resaleGainPerUnit } from "./estimate";
import { SupplyIntelPool } from "./pool";

/** What the brand's grade settings say about a grade. */
export enum SupplyIntelGradeStatus {
  /** The grade is flagged for review rather than accepted or rejected. */
  FlaggedForReview = "flagged_for_review",
  /** Accepted, but the grade doesn't restock. */
  NotSoldAsNew = "not_sold_as_new",
  /** The brand rejects this grade. */
  Rejected = "rejected",
  /** The grade restocks: sell as new. */
  SellAsNew = "sell_as_new",
  /** Blank, or no matching setting. */
  Unknown = "unknown",
}

export enum SupplyIntelNextStep {
  AlreadyRouted = "already_routed",
  BrandRejected = "brand_rejected",
  Excluded = "excluded",
  KeepOnBrandStore = "keep_on_brand_store",
  Liquidate = "liquidate",
  NeedsGrade = "needs_grade",
  NeedsPrice = "needs_price",
  OffPriceLot = "off_price_lot",
  Resale = "resale",
  ResalePreOwned = "resale_pre_owned",
  ReviewHold = "review_hold",
}

/**
 * Units of one SKU, grade and pool that share a timer state. Callers split a
 * SKU's units by `pastRefulfillmentTimer` so fresh units are never pulled from
 * the brand's store along with old ones.
 */
export interface SupplyIntelGroup {
  currency: string | null;
  /** Why the category isn't resold, from `supplyIntelCategoryExclusion`; null when it is. */
  excludedCategory: string | null;
  gradeStatus: SupplyIntelGradeStatus;
  listPrice: number | null;
  pastRefulfillmentTimer: boolean;
  pool: SupplyIntelPool;
  /** From `supplyIntelRoutedTo`; null unless a decision routes the units away from restock. */
  routedTo: string | null;
  units: number;
}

interface SupplyIntelRecommendation {
  configVersion: string;
  reason: string;
  step: SupplyIntelNextStep;
}

/** A brand's setting for one grade, with `restocks` from `mappingRestocks` in grade settings. */
interface SupplyIntelGradeSetting {
  acceptance: "accept" | "flag_for_review" | "reject";
  restocks: boolean;
}

export function supplyIntelGradeStatus(
  setting: SupplyIntelGradeSetting | null,
): SupplyIntelGradeStatus {
  if (setting === null) {
    return SupplyIntelGradeStatus.Unknown;
  }
  if (setting.acceptance === "reject") {
    return SupplyIntelGradeStatus.Rejected;
  }
  if (setting.acceptance === "flag_for_review") {
    return SupplyIntelGradeStatus.FlaggedForReview;
  }
  return setting.restocks
    ? SupplyIntelGradeStatus.SellAsNew
    : SupplyIntelGradeStatus.NotSoldAsNew;
}

/**
 * Sell-as-new units outside a for-sale bin are an anomaly to review. Units the
 * brand doesn't sell as new are reserved by design, so the pool doesn't hold
 * them back from resale.
 */
const SELL_AS_NEW_HOLD_REASONS: Partial<Record<SupplyIntelPool, string>> = {
  [SupplyIntelPool.DamagedArea]:
    "In a damaged-area bin despite a sell-as-new grade",
  [SupplyIntelPool.HeldNonSellable]:
    "Held in a non-sellable bin, such as a brand holding area; needs the brand's release",
  [SupplyIntelPool.ReservedInSellableBin]:
    "In a sellable bin without a restock decision",
};

/** The next step for a group of returned units; rules run in order and the first match wins. */
export function recommendNextStep(
  group: SupplyIntelGroup,
  config: SupplyIntelConfig,
): SupplyIntelRecommendation {
  const recommend = recommender(config);

  // A routing decision already made stands; exclusion only steers units nobody has routed.
  if (group.routedTo !== null) {
    return recommend(
      SupplyIntelNextStep.AlreadyRouted,
      `Already routed to ${group.routedTo}`,
    );
  }
  if (group.excludedCategory !== null) {
    return recommend(SupplyIntelNextStep.Excluded, group.excludedCategory);
  }
  switch (group.gradeStatus) {
    case SupplyIntelGradeStatus.Rejected:
      return recommend(
        SupplyIntelNextStep.BrandRejected,
        "The brand rejects this grade",
      );
    case SupplyIntelGradeStatus.FlaggedForReview:
      return recommend(
        SupplyIntelNextStep.ReviewHold,
        "The brand flags this grade for review",
      );
    case SupplyIntelGradeStatus.Unknown:
      return recommend(
        SupplyIntelNextStep.NeedsGrade,
        "Grade is blank or has no setting",
      );
    case SupplyIntelGradeStatus.NotSoldAsNew:
      return gatedResale(group, config, {
        preOwned: true,
        reason:
          group.pool === SupplyIntelPool.HeldNonSellable
            ? "Not sold as new; resell as Pre-owned with real photos after the brand releases it"
            : "Not sold as new; resell as Pre-owned with real photos",
        step: SupplyIntelNextStep.ResalePreOwned,
      });
    case SupplyIntelGradeStatus.SellAsNew:
      break;
  }
  const holdReason = SELL_AS_NEW_HOLD_REASONS[group.pool];
  if (holdReason !== undefined) {
    return recommend(SupplyIntelNextStep.ReviewHold, holdReason);
  }
  if (!group.pastRefulfillmentTimer) {
    return recommend(
      SupplyIntelNextStep.KeepOnBrandStore,
      `For sale on the brand's store for under ${config.refulfillmentTimerDays} days`,
    );
  }
  return gatedResale(group, config, {
    preOwned: false,
    reason: `Not refulfilled after ${config.refulfillmentTimerDays} days`,
    step: SupplyIntelNextStep.Resale,
  });
}

function recommender(config: SupplyIntelConfig) {
  return (
    step: SupplyIntelNextStep,
    reason: string,
  ): SupplyIntelRecommendation => ({
    configVersion: config.version,
    reason,
    step,
  });
}

/** Resale when listing beats liquidating at the expected sell-through; else a lot or liquidation. */
function gatedResale(
  group: SupplyIntelGroup,
  config: SupplyIntelConfig,
  resale: { preOwned: boolean; reason: string; step: SupplyIntelNextStep },
): SupplyIntelRecommendation {
  const recommend = recommender(config);
  const priceUsd = listPriceUsd(group, config);
  if (priceUsd === null) {
    return recommend(
      SupplyIntelNextStep.NeedsPrice,
      "No list price in a known currency",
    );
  }
  const gain = resaleGainPerUnit({
    listPriceUsd: priceUsd,
    params: config.estimate,
    preOwned: resale.preOwned,
    sellThrough: config.expectedSellThrough,
    sellThroughHorizonDays: config.sellThroughHorizonDays,
    windowDays: config.resaleWindowDays,
  });
  if (gain > 0) {
    return recommend(resale.step, resale.reason);
  }
  const belowGate = `Listing doesn't beat liquidating at ${Math.round(config.expectedSellThrough * 100)}% sell-through within ${config.sellThroughHorizonDays} days`;
  if (group.units >= config.minLotUnits) {
    return recommend(
      SupplyIntelNextStep.OffPriceLot,
      `${belowGate}; ${group.units} units make an off-price lot`,
    );
  }
  return recommend(SupplyIntelNextStep.Liquidate, belowGate);
}

/** List price in USD, or null when the price or its currency rate is unknown. */
export function listPriceUsd(
  group: Pick<SupplyIntelGroup, "currency" | "listPrice">,
  config: SupplyIntelConfig,
): number | null {
  if (group.listPrice === null || group.currency === null) {
    return null;
  }
  const rate = config.usdPerUnitOfCurrency[group.currency.toUpperCase()];
  if (rate === undefined) {
    return null;
  }
  return group.listPrice * rate;
}
